import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { CircularProgress, Typography, Box } from '@mui/material';

const DEFAULT_COLORS = [
  '#003781',
  '#0057A8',
  '#1976D2',
  '#42A5F5',
  '#90CAF9',
  '#1565C0',
];

/**
 * Custom tooltip — white card, rounded, shadow, coloured dots per series.
 */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: 'none',
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography variant="body2" fontWeight={700} mb={0.75} sx={{ color: '#003781' }}>
        {label}
      </Typography>
      {payload.map((entry) => (
        <Box key={entry.dataKey} display="flex" alignItems="center" gap={1} mb={0.25}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: entry.fill || entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ color: '#6b7280' }}>
            {entry.name}:&nbsp;
            <span style={{ fontWeight: 600, color: '#003781' }}>{entry.value}</span>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * Custom legend — rounded colour dots.
 */
const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null;
  return (
    <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" mt={0.5}>
      {payload.map((entry) => (
        <Box key={entry.value} display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ color: '#374151', fontWeight: 500 }}>
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * DashboardBarChart — grouped bar chart using Recharts.
 *
 * Props:
 *   data          — array of objects
 *   xKey          — string key used for the X axis (e.g. 'quarter')
 *   series        — [{ dataKey, name, color }]
 *   title         — string
 *   height        — number (default 300)
 *   loading       — bool
 *   yLabel        — string (default 'Count')
 *   yTickInterval   — number (e.g. 10 → ticks 0,10,20,... up to rounded max)
 *   yTickFormatter  — function (value) => string  (e.g. v => `${v}%`)
 */
const DashboardBarChart = ({
  data = [],
  xKey = 'quarter',
  series = [],
  title = '',
  height = 300,
  loading = false,
  yLabel = 'Count',
  yTickInterval = null,
  yTickFormatter = null,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  // Build explicit Y-axis ticks when yTickInterval is set
  let yAxisProps = {};
  if (yTickInterval) {
    const allValues = data.flatMap((row) =>
      series.map((s) => Number(row[s.dataKey]) || 0)
    );
    const maxVal = Math.max(0, ...allValues);
    const ceiling = Math.ceil(maxVal / yTickInterval) * yTickInterval || yTickInterval;
    const ticks = [];
    for (let t = 0; t <= ceiling; t += yTickInterval) ticks.push(t);
    yAxisProps = { ticks, domain: [0, ceiling] };
  }

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          mb={1.5}
          sx={{ color: '#374151' }}
        >
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="#e8eef7" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={yTickFormatter || undefined}
            {...yAxisProps}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,55,129,0.05)' }} />
          <Legend content={<CustomLegend />} />
          {series.map((s, idx) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              fill={s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DashboardBarChart;
